"use client";

import { c as _c } from "react/compiler-runtime";
import BackIcon from "@/assets/icons/back-icon.svg";
import ChevronLeftIcon from "@/assets/icons/chevron-left.svg";
import ChevronRightIcon from "@/assets/icons/chevron-right.svg";
import { GlassButton } from "@/shared/ui";
import { cn } from "@/lib/utils";
import { isAtMaxMonth, isAtMinMonth } from "../../month-bounds";
import styles from "./section-header.module.scss";
export type SectionHeaderProps = {
  title: string;
  onBack?: () => void;
  backLabel?: string;
  onPrev?: () => void;
  onNext?: () => void;
  /** When set with prev/next handlers, disables nav at the 4-month-ago / current-month bounds. */
  month?: Date;
  prevLabel?: string;
  nextLabel?: string;
  /** Replaces the default prev/next nav buttons when provided. */
  rightContent?: React.ReactNode;
  className?: string;
};

/**
 * Icon button matching the 4 Figma states (238:3498):
 *   Default  — transparent bg, white/70 icon
 *   Hover    — bg-white/8, white icon
 *   Pressed  — bg-white/12, white icon
 *   Disabled — transparent bg, white/30 icon, non-interactive
 */
function NavIconButton(t0) {
  const $ = _c(6);
  const {
    label,
    onClick,
    disabled,
    children,
    nodeId
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== disabled || $[2] !== label || $[3] !== nodeId || $[4] !== onClick) {
    t1 = <button type="button" aria-label={label} onClick={onClick} disabled={disabled} data-node-id={nodeId} className={styles.navButton}>{children}</button>;
    $[0] = children;
    $[1] = disabled;
    $[2] = label;
    $[3] = nodeId;
    $[4] = onClick;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}

/**
 * Section header with a bold title and optional prev/next navigation arrows.
 * StremHub UI Kit node 3375:83320. Button states from node 238:3498.
 */
export function SectionHeader(t0) {
  const $ = _c(26);
  const {
    title,
    onBack,
    backLabel: t1,
    onPrev,
    onNext,
    month,
    prevLabel: t2,
    nextLabel: t3,
    rightContent,
    className
  } = t0;
  const backLabel = t1 === undefined ? "Go back" : t1;
  const prevLabel = t2 === undefined ? "Previous" : t2;
  const nextLabel = t3 === undefined ? "Next" : t3;
  let t4;
  if ($[0] !== month) {
    t4 = month ? isAtMinMonth(month) : false;
    $[0] = month;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const atMin = t4;
  let t5;
  if ($[2] !== month) {
    t5 = month ? isAtMaxMonth(month) : false;
    $[2] = month;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const atMax = t5;
  let t6;
  if ($[4] !== className) {
    t6 = cn(styles.root, className);
    $[4] = className;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== backLabel || $[7] !== onBack) {
    t7 = onBack && <span className={styles.backButtonWrap}><GlassButton size="sm" aria-label={backLabel} onClick={onBack} className={styles.backButton}><BackIcon className={styles.backIcon} aria-hidden={true} /></GlassButton></span>;
    $[6] = backLabel;
    $[7] = onBack;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== title) {
    t8 = <h2 className={styles.title} data-node-id="3375:83321">{title}</h2>;
    $[9] = title;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t7 || $[12] !== t8) {
    t9 = <div className={styles.leftGroup}>{t7}{t8}</div>;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== atMax || $[15] !== atMin || $[16] !== nextLabel || $[17] !== onNext || $[18] !== onPrev || $[19] !== prevLabel || $[20] !== rightContent) {
    t10 = rightContent ?? ((onPrev ?? onNext) && <div className={styles.navGroup} data-node-id="3375:83322"><NavIconButton label={prevLabel} onClick={onPrev} disabled={!onPrev || atMin} nodeId="3375:83323"><ChevronLeftIcon className={styles.navIcon} aria-hidden={true} /></NavIconButton><NavIconButton label={nextLabel} onClick={onNext} disabled={!onNext || atMax} nodeId="3375:83324"><ChevronRightIcon className={styles.navIcon} aria-hidden={true} /></NavIconButton></div>);
    $[14] = atMax;
    $[15] = atMin;
    $[16] = nextLabel;
    $[17] = onNext;
    $[18] = onPrev;
    $[19] = prevLabel;
    $[20] = rightContent;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t6 || $[24] !== t9) {
    t11 = <div className={t6} data-node-id="3375:83320">{t9}{t10}</div>;
    $[22] = t10;
    $[23] = t6;
    $[24] = t9;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  return t11;
}